import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext.tsx';
import { useToast } from '@/hooks/use-toast.ts';
import bankAccountService from "@bankAccountService";
import {BankAccount} from "@/integrations/supabase/account.models.ts";

export interface BankAccountDetailsInput {
  // Account fields (passed through to accounts insert)
  accountData: {
    name: string,
    nickname: string,
    account_number: string,
    type: BankAccountType,
    currency: string,
    balance: number,
    status: string
  }
  // Bank fields
  bankId?: string;       // set when selecting existing bank
  bankName?: string;     // set when creating new bank
  bankAddress?: string;  // optional for both new and existing
  // Bank account detail fields
  sortCode?: string;
  iban?: string;
  swiftCode?: string;
  routingNumber?: string;
  transitNumber?: string;
  institutionNumber?: string;
}

export function useCreateBankAccountWithDetails() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (input: BankAccountDetailsInput): Promise<BankAccount> => {
      return await bankAccountService.createBankAccount({
        userId: user.id,
        bankDetails: {
          bankId: input.bankId,
          name: input.bankName,
          address: input.bankAddress,
        },
        details: {
          name: input.accountData.name,
          nickname: input.accountData.nickname,
          accountNumber: input.accountData.account_number,
          accountType: input.accountData.type,
          balance: input.accountData.balance,
          currency: input.accountData.currency,
          status: input.accountData.status
        },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounts'] });
      qc.invalidateQueries({ queryKey: ['banks'] });
    },
    onError: () => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to create account. Please try again.',
      });
    },
  });
}
